import { openAuthModal } from "../utils/auth-modal.events";

/**
 * Lightweight REST wrapper used by feature services.
 * We pass the token in the header from the caller rather than reading user state
 * inside this service so request behavior stays explicit and testable.
 */
export class RestAPI {
    private defaultHeaders: { [key: string]: string } = {
        "Content-Type": "application/json",
        'Access-Control-Allow-Origin': '*'
    };

    /**
     * Send a JSON POST request, optionally adding per-call headers such as conversion attribution.
     */
    async post(
        url: string,
        body: any,
        token: string | null | undefined = null,
        extraHeaders: Record<string, string> = {},
        suppressAuthModalOnUnauthorized = false
    ) {
        const response = await fetch(url, {
            headers: this.getHeaders(token, extraHeaders),
            body: body == null ? undefined : JSON.stringify(body),
            method: 'POST',
        });
        
        if (!await this.isNotAuthorizedReponse(response, suppressAuthModalOnUnauthorized)) {
            const error = new Error("Token has expired, please re-login or you are not authorized to perform this action.");
            (error as { status?: number }).status = response.status;
            throw error;
        }

        let responseData: any = null;
        try {
            responseData = await response.json();
        } catch {
            // Successful create endpoints may legitimately return an empty body.
        }

        if (response.ok) {
            return responseData ?? (response.status === 201
                ? { message: 'Form submitted successfully' }
                : null);
        }

        const errorMessage = this.getErrorMessage(responseData, "Error processing request.");
        const error = new Error(errorMessage);
        (error as { response?: unknown; status?: number; data?: unknown }).response = responseData;
        (error as { response?: unknown; status?: number; data?: unknown }).status = response.status;
        (error as { response?: unknown; status?: number; data?: unknown }).data = responseData;
        throw error;
    }

    /**
     * Send a JSON POST request when no response body is expected, optionally adding per-call headers.
     */
    async postVoid(
        url: string,
        body: any,
        token: string | null | undefined = null,
        extraHeaders: Record<string, string> = {}
    ) {
        const response = await fetch(url, {
            headers: this.getHeaders(token, extraHeaders),
            body: JSON.stringify(body),
            method: 'POST',
        });

        if (!await this.isNotAuthorizedReponse(response)) {
            throw new Error("Token has expired, please re-login or you are not authorized to perform this action.");
        }

        if (response.ok) {
            return;
        }

        let responseData: any = null;
        try {
            responseData = await response.json();
        } catch {
            // ignore parse errors for empty bodies
        }

        const errorMessage = this.getErrorMessage(responseData, "Error processing request.");
        throw new Error(errorMessage);
    }

    /**
     * Send a JSON GET request.
     */
    async get(url: string, token: string | null | undefined = null, signal?: AbortSignal) {
        const response = await fetch(url, {
            headers: this.getHeaders(token),
            method: 'GET',
            ...(signal ? { signal } : {})
        });

        if (!await this.isNotAuthorizedReponse(response)) {
            const error = new Error("Token has expired, please re-login or you are not authorized to perform this action.");
            (error as { status?: number }).status = response.status;
            throw error;
        }

        let responseData: any = null;
        try {
            responseData = await response.json();
        } catch {
            // Some GET endpoints may return empty bodies during failures. Use the standard fallback below.
        }

        if (response.ok) {
            return responseData;
        }

        const errorMessage = this.getErrorMessage(responseData, "Error processing request.");
        const error = new Error(errorMessage);
        (error as { response?: unknown; status?: number }).response = responseData;
        (error as { response?: unknown; status?: number }).status = response.status;
        throw error;
    }

    /**
     * Send a JSON PUT request and return the typed response, optionally adding per-call headers.
     */
    async put<TResponse>(
        url: string,
        body: any,
        token: string | null | undefined = null,
        extraHeaders: Record<string, string> = {}
    ): Promise<TResponse> {
        const response = await fetch(url, {
            headers: this.getHeaders(token, extraHeaders),
            body: body == null ? undefined : JSON.stringify(body),
            method: 'PUT'
        });

        if (!await this.isNotAuthorizedReponse(response)) {
            throw new Error("Token has expired, please re-login or you are not authorized to perform this action.");
        }

        let responseData: any = null;
        try {
            responseData = await response.json();
        } catch {
            // Some update endpoints return 204/empty bodies. Treat as success when response.ok.
        }

        if (response?.ok) {
            return responseData;
        }

        const errorMessage = this.getErrorMessage(responseData, "Error processing request.");
        const error = new Error(errorMessage);
        (error as { response?: unknown; status?: number }).response = responseData;
        (error as { response?: unknown; status?: number }).status = response.status;
        throw error;
    }

    /**
     * Send a JSON PUT request when no response body is expected, optionally adding per-call headers.
     */
    async putVoid(
        url: string,
        body: any,
        token: string | null | undefined = null,
        extraHeaders: Record<string, string> = {}
    ) {
        const response = await fetch(url, {
            headers: this.getHeaders(token, extraHeaders),
            body: JSON.stringify(body),
            method: 'PUT'
        });

        if (!await this.isNotAuthorizedReponse(response)) {
            throw new Error("Token has expired, please re-login or you are not authorized to perform this action.");
        }

        if (response.ok) {
            return;
        }

        let responseData: any = null;
        try {
            responseData = await response.json();
        } catch {
            // ignore parse errors for empty bodies
        }

        const errorMessage = this.getErrorMessage(responseData, "Error processing request.");
        throw new Error(errorMessage);
    }

    /**
     * Send a JSON DELETE request.
     */
    async delete(url: string, token: string | null | undefined = null) {
        const response = await fetch(url, {
            headers: this.getHeaders(token),
            method: 'DELETE'
        });

        if (!await this.isNotAuthorizedReponse(response)) {
            throw new Error("Token has expired, please re-login or you are not authorized to perform this action.");
        }

        let responseData: any = null;
        try {
            responseData = await response.json();
        } catch {
            // Many DELETE endpoints return empty bodies. Treat empty success as true.
        }

        if (response.ok) {
            return responseData ?? true;
        }

        const errorMessage = this.getErrorMessage(responseData, "Error processing request.");
        throw new Error(errorMessage);
    }

    /**
     * @param token If provided we inject it as a Bearer token; otherwise default headers are used.
     * @returns Headers for API requests.
     */
    private getHeaders(token: string | null | undefined, extraHeaders: Record<string, string> = {}) {
        const headers = {...this.defaultHeaders, ...extraHeaders};
        if (!!token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return headers;
    }

    /**
     * Opens the auth modal for 401s to keep auth flow consistent across calls.
     */
    private async isNotAuthorizedReponse(
        response: Response,
        suppressAuthModalOnUnauthorized = false
    ): Promise<boolean> {
        if (response.status === 401) {
            if (!suppressAuthModalOnUnauthorized) {
                openAuthModal('paid_content', window.location.href);
            }
            return false;
        }

        return true;
    }

    private getErrorMessage(responseData: any, fallbackMessage: string) {
        if (typeof responseData === 'string' && responseData.trim()) {
            return responseData;
        }
        const detail = responseData?.Detail ?? responseData?.detail;
        if (typeof detail === 'string' && detail.trim()) {
            return detail;
        }
        if (responseData?.Title) {
            return `Error processing request: ${responseData.Title}`;
        }
        const errors = responseData?.Errors ?? responseData?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
            const firstError = errors[0];
            if (typeof firstError === 'string') {
                return firstError;
            }
            const description = firstError?.Description || firstError?.description;
            if (description) {
                return description;
            }
        }
        return fallbackMessage;
    }
}
